import { Source } from "duck/types";
import type { TFunction } from "i18next";

import { Claim } from "shared/api/claims/api";
import {
  createIssuePin,
  NewIssueRequest,
  PinType,
} from "shared/api/issues/api";
import { SignalEventsOccurrence } from "shared/api/signalEvents/api";
import { SmartGroup } from "shared/api/smartGroups/api";
import { EventTypeEnum } from "shared/types";
import { cloneObject, randomID } from "shared/utils";

import {
  LandingPageConfig,
  LandingPageViewConfig,
} from "pages/shared/Dashboard/types";
import { getDefaultLandingPageViewConfig } from "pages/shared/Dashboard/utils";

import { DEFAULT_FILTER_BUILDER_STATE } from "features/ui/Filters/FilterBuilder/constants";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import {
  convertInLastToAfterDateFilters,
  filterBuilderQueryToFilterBuilderState,
  mergeFilterGroupStates,
} from "features/ui/Filters/FilterBuilder/utils";
import { FilterOperator } from "features/ui/Filters/types";

import { SmartGroupPinType, TablePinType } from "./components/pins/types";
import {
  getPinTypeDefaultTitle,
  INVESTIGATOR_EVENT_FILTER,
  INVESTIGATOR_EVENT_TYPE,
  INVESTIGATOR_VEHICLE_FILTER,
} from "./constants";
import { PlaceholderIssue } from "./types";

export const createSmartGroupFilter = (id: string): FilterGroupState => ({
  type: "group",
  id: `group-${randomID()}`,
  anyAll: "all",
  children: [
    {
      type: "row",
      id: `row-${randomID()}`,
      attribute: "smartGroups",
      operator: FilterOperator.IN,
      values: [id],
    },
  ],
});

const getEventFiltersWithoutSmartGroup = (eventsFilter: string) =>
  convertInLastToAfterDateFilters(
    filterBuilderQueryToFilterBuilderState(eventsFilter) ??
      DEFAULT_FILTER_BUILDER_STATE
  );

export const getEventFilters = (eventsFilter: string, smartGroupId?: string) =>
  mergeFilterGroupStates(
    smartGroupId
      ? createSmartGroupFilter(smartGroupId)
      : DEFAULT_FILTER_BUILDER_STATE,
    getEventFiltersWithoutSmartGroup(eventsFilter)
  );

export const getVehicleFilters = (vehicleFilter: string) =>
  convertInLastToAfterDateFilters(
    filterBuilderQueryToFilterBuilderState(vehicleFilter) ??
      DEFAULT_FILTER_BUILDER_STATE
  );

export const createInitialIssue = (
  eventType: EventTypeEnum,
  eventFilter: string,
  vehicleFilter: string,
  t: TFunction,
  smartGroup?: SmartGroup
) => {
  const issue: NewIssueRequest = {
    name: smartGroup?.name ?? t("investigator.untitled_investigation_title"),
    description: "",
    atRiskPopulationFilter: vehicleFilter,
    claimFilter: eventType === EventTypeEnum.CLAIM ? eventFilter : undefined,
    signalEventOccurrencesFilter:
      eventType === EventTypeEnum.SIGNAL_EVENT ? eventFilter : undefined,
  };

  if (smartGroup) {
    issue.smartGroupID = smartGroup.ID;
  }

  return issue;
};

export const ensureSmartGroupFilterFirstWithName = (
  filterGroupState: FilterGroupState
): FilterGroupState => {
  const clonedState = cloneObject(filterGroupState);

  // Find the smartGroups filter
  const smartGroupFilterIndex = clonedState.children.findIndex(
    (child) => child.type === "row" && child.attribute === "smartGroups"
  );

  if (smartGroupFilterIndex > 0) {
    // Remove the smartGroups filter from its current position
    const smartGroupFilter = clonedState.children.splice(
      smartGroupFilterIndex,
      1
    )[0];
    // Add it to the beginning
    clonedState.children.unshift(smartGroupFilter);
  }

  return clonedState;
};

export const createPin = async (
  pin: SmartGroupPinType,
  issueId: string,
  t: TFunction
) =>
  await createIssuePin({
    issueID: issueId,
    type: pin.type,
    data: pin.data,
    title: pin.title ?? getPinTypeDefaultTitle(pin.type, t),
  });

export const getPlaceholderPin = (
  issueID: string | undefined
): TablePinType => ({
  ID: "new",
  createdAt: "",
  updatedAt: "",
  issueID: issueID ?? "new",
  type: PinType.TABLE,
  isDefault: true,
  data: {
    filters: undefined,
  },
});

export const saveIssueToLocalStorage = (
  issue: PlaceholderIssue,
  eventType: EventTypeEnum
) => {
  const eventFilter =
    eventType === EventTypeEnum.CLAIM
      ? issue.claimFilter
      : issue.signalEventOccurrencesFilter;
  const vehiclesFilter = issue.atRiskPopulationFilter;
  localStorage.setItem(INVESTIGATOR_EVENT_FILTER, eventFilter || "");
  localStorage.setItem(INVESTIGATOR_VEHICLE_FILTER, vehiclesFilter || "");
  localStorage.setItem(INVESTIGATOR_EVENT_TYPE, eventType);
};
export const getViewConfig = (
  landingPage?: LandingPageConfig,
  smartGroup?: SmartGroup,
  eventTypeQs?: string,
  viewTitle?: string
): Partial<LandingPageViewConfig> => {
  const viewConfigBySmartGroup = landingPage?.views?.find(
    (v) => smartGroup && v.smartGroupType === smartGroup.eventType
  );
  if (viewConfigBySmartGroup) {
    return viewConfigBySmartGroup;
  }

  const viewConfigByViewTitle = landingPage?.views?.find(
    ({ title }) => title === viewTitle
  );

  if (viewConfigByViewTitle) {
    return viewConfigByViewTitle;
  }

  const viewConfigByQsEventType = landingPage?.views?.find(
    ({ eventType }) => eventType === eventTypeQs
  );

  if (viewConfigByQsEventType) {
    return viewConfigByQsEventType;
  }

  const viewConfigByDefaultEvent = landingPage?.views?.find(
    ({ eventType }) => eventType === EventTypeEnum.CLAIM
  );

  if (viewConfigByDefaultEvent) {
    return viewConfigByDefaultEvent;
  }

  return getDefaultLandingPageViewConfig();
};

export const createPhotosFilter = (): FilterGroupState => ({
  type: "group",
  id: `group-${randomID()}`,
  anyAll: "all",
  children: [
    {
      type: "row",
      id: `row-${randomID()}`,
      attribute: "vehiclePhotos",
      operator: FilterOperator.IS_NOT_EMPTY,
      values: ["null"],
    },
  ],
});

export const mapEventTypeToSource = (eventType: EventTypeEnum): Source => {
  switch (eventType) {
    case EventTypeEnum.CLAIM:
      return Source.CLAIM;
    case EventTypeEnum.SIGNAL_EVENT:
      return Source.SIGNAL_EVENT_OCCURRENCE;
    default:
      return Source.CLAIM;
  }
};

export const getPerVinPhotos = (claims: Claim[] | SignalEventsOccurrence[]) => {
  const visited = new Set<string>();

  return claims
    .map((claim) => {
      if (visited.has(claim.VIN)) {
        return;
      }

      visited.add(claim.VIN);

      return claim.vehiclePhotos;
    })
    .filter((vehiclePhotos) => vehiclePhotos !== undefined)
    .flat();
};

export const hasAttributePresent = (
  filterState: FilterGroupState,
  attribute: string
) =>
  filterState.children.some(
    (child) => child.type === "row" && child.attribute === attribute
  );
